/* @flow */
/**
 * Redux Toolkit slice for large-file scheduler jobs and immediate report export execution.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import { poSearchResultActions } from '../poSearch/poSearchResultSlice';
import { poReportActions } from '../poReport/poReportSlice';
import {
  conversationThreadReportActions
} from '../conversationThreadReport/conversationThreadReportSlice';
import {
  gacChangeReportActions
} from '../gacChangeHistoryReport/gacChangeHistoryReportSlice';
import {
  lineSummaryActions
} from '../lineSummaryReport/lineSummaryReportSlice';
import { poChangeReportActions } from '../poChangeReport/poChangeReportSlice';
import {
  ppmReportRequestActions
} from '../ppmReport/ppmReportRequestSlice';
import {
  airFreightReportActions
} from '../airFreightReport/airFreightReportSlice';

const name = 'scheduler';

// Maps scheduler report types to the export action used for immediate execution.
const IMMEDIATE_EXPORT_REPORT_BY_TYPE = {
  POSEARCH: ppmReportRequestActions.exportPOReport,
  ITEMSUMMARYREPORT: lineSummaryActions.exportPOReport,
  CHANGEREPORT: poChangeReportActions.exportPoChangeReport,
  COLLABREPORT: poReportActions.exportPOReport,
  GACCHANGEHISTORYREPORT: gacChangeReportActions.exportGACChangeHistoryReport,
  CONVERSATIONREPORT: conversationThreadReportActions.exportConversationThreadReport,
  AIRFREIGHTREPORT: airFreightReportActions.exportAirFreightReport
};

function createInitialState() {
  return {
    schedulerData: {}
  };
}
export const initialState = createInitialState();

/**
 * Stores scheduler job data in state.
 */
function setSchedulerJob(state: Object, action: Object) {
  return {
    ...state,
    schedulerData: action.payload
  };
}

/**
 * Resets scheduler job data to the initial scheduler state.
 */
function resetSchedulerData(state: Object) {
  return {
    ...state,
    schedulerData: initialState
  };
}

/**
 * Collects reducers that manage scheduler state.
 */
function createReducers() {
  return {
    setSchedulerJob,
    resetSchedulerData
  };
}

export const reducers = createReducers();
const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Resets PO search state after scheduler changes the active saved search.
 */
function resetPoSearchIfSavedSearchMatches(dispatch, schedulerData, state) {
  // Only clear PO search when the scheduled job references the active saved search.
  if (schedulerData?.saveSearchID === state.PoSearch.savedSearchId) {
    dispatch(poSearchResultActions.resetPOSearchRequest());
    dispatch(poSearchResultActions.resetPoSearchResult());
    dispatch(poSearchResultActions.resetFiltersToInitialState());
  }
}

/**
 * Creates a thunk that submits scheduler jobs to createSchedular.
 */
function callCreateSchedulerApi() {
  return createAsyncThunk(
    `${name}/callCreateSchedulerApi`,
    async ({
      schedulerData,
      callbackFn
    }, { getState, dispatch }) => {
      const data = {
        // Scheduler APIs expect job details wrapped in the largeFileScheduleData array.
        largeFileScheduleData: [{ ...schedulerData }]
      };
      dispatch(toggleSpinner(true));
      request({
        method: 'post',
        api: 'createSchedular',
        data,
        suppressErrorDialog: true
      }, dispatch, getState).then((response) => {
        if (callbackFn) {
          callbackFn(response.data);
        }
        const state = getState();
        dispatch(toggleSpinner(false));
        resetPoSearchIfSavedSearchMatches(dispatch, schedulerData, state);
      }).catch((error) => {
        if (callbackFn) {
          callbackFn(null, error);
        }
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Creates a thunk that immediately dispatches an export action for CSV output.
 */
function callImmediatlySchedulerApi() {
  return createAsyncThunk(
    `${name}/callImmediatlySchedulerApi`,
    async (
      {
        callbackFn,
        exportReport
      }, { dispatch }
    ) => {
      dispatch(exportReport({
        callback: (response) => {
          if (callbackFn) {
            callbackFn(response?.data);
          }
        },
        filetype: 'CSV'
      }));
    }
  );
}

/**
 * Creates a thunk that routes scheduler requests to immediate export or scheduler creation.
 */
function createSchedulerJob() {
  return createAsyncThunk(
    `${name}/createSchedulerJob`,
    async ({
      schedulerData, callbackFn,
      type = 'POSEARCH',
      schedulerCallImmediatlySchedulerApi,
      schedulercallCreateSchedulerApi
    }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      if (schedulerData?.isExecuteImmediatly) {
        // Unknown report types fall back to the PO search export action.
        const exportReport = IMMEDIATE_EXPORT_REPORT_BY_TYPE[type]
          ?? ppmReportRequestActions.exportPOReport;
        dispatch(schedulerCallImmediatlySchedulerApi({
          callbackFn, exportReport
        }));
      } else {
        dispatch(schedulercallCreateSchedulerApi({ schedulerData, callbackFn }));
      }
    }
  );
}

/**
 * Creates a thunk that updates scheduler jobs through updateSchedular.
 */
function updateSchedulerJob() {
  return createAsyncThunk(
    `${name}/updateSchedulerJob`,
    async ({
      schedulerData, callback
    }, { dispatch, getState }) => {
      dispatch(toggleSpinner(true));
      const data = {
        type: 'update',
        largeFileScheduleData: [{ ...schedulerData }]
      };
      request({
        method: 'put',
        api: 'updateSchedular',
        data,
        suppressErrorDialog: true
      }, dispatch, getState).then((response) => {
        if (callback) {
          callback(response.data);
        }
        const state = getState();
        dispatch(toggleSpinner(false));
        resetPoSearchIfSavedSearchMatches(dispatch, schedulerData, state);
      }).catch((error) => {
        if (callback) {
          callback(null, error);
        }
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Instantiates async scheduler actions exposed by the slice.
 */
function createExtraActions() {
  return {
    callCreateSchedulerApi: callCreateSchedulerApi(),
    updateSchedulerJob: updateSchedulerJob(),
    createSchedulerJob: createSchedulerJob(),
    callImmediatlySchedulerApi: callImmediatlySchedulerApi()
  };
}

export const extraActions = createExtraActions();

export const schedulerActions = { ...actions, ...extraActions };
export const schedulerReducer = slice.reducer;
